import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "widget-upcoming-view" }
const _hoisted_2 = { class: "widget-header" }
const _hoisted_3 = { class: "widget-header__title" }
const _hoisted_4 = { class: "widget-header__title-accent" }
const _hoisted_5 = { class: "widget-header__title-plain" }
const _hoisted_6 = { class: "widget-header__actions" }
const _hoisted_7 = { class: "widget-header__meta-label" }
const _hoisted_8 = {
  key: 0,
  class: "widget-state"
}
const _hoisted_9 = {
  key: 1,
  class: "widget-state widget-state--error"
}
const _hoisted_10 = {
  key: 2,
  class: "widget-state"
}
const _hoisted_11 = {
  key: 3,
  class: "widget-carousel"
}
const _hoisted_12 = ["aria-label"]
const _hoisted_13 = ["aria-label"]

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import WidgetLotteryCard from '@/components/cards/WidgetLotteryCard.vue'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'
import { useAuthStore } from '@/stores/auth'
import { initializeTheme } from '@/composables/useTheme'
import type { UpcomingDraw } from '@/types/lottery'
import { iframeCommunication } from '@/utils/iframeCommunication'
import '@/styles/widget.css'


export default /*@__PURE__*/_defineComponent({
  __name: 'WidgetUpcomingView',
  setup(__props) {

const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()

const titleParts = computed(() => {
  const raw = sectionTitle.value.trim()
  const spaceIndex = raw.indexOf(' ')
  if (spaceIndex === -1) {
    return { lead: raw, rest: '' }
  }
  return {
    lead: raw.slice(0, spaceIndex),
    rest: raw.slice(spaceIndex),
  }
})

const carouselRef = ref<HTMLElement | null>(null)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

const updateScrollState = () => {
  const track = carouselRef.value
  canScrollPrev.value = !!track && track.scrollLeft > 1
  canScrollNext.value = !!track && track.scrollLeft + track.clientWidth < track.scrollWidth - 1
}
const upcomingDrawsStore = useUpcomingDrawsStore()

const draws = computed(() => upcomingDrawsStore.draws)

const showAllLink = computed(() => {
  const raw = Array.isArray(route.query.showAll) ? route.query.showAll[0] : route.query.showAll
  if (typeof raw === 'string') {
    return raw.trim().toLowerCase() !== 'false'
  }
  return true
})

const sectionTitle = computed(() => {
  const raw = Array.isArray(route.query.title) ? route.query.title[0] : route.query.title
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return t('sections.luckyNumbers')
})

const loading = computed(() => upcomingDrawsStore.loading)
const error = computed(() => upcomingDrawsStore.error)

const getScrollStep = () => {
  if (!carouselRef.value) return 166
  const firstItem = carouselRef.value.firstElementChild as HTMLElement | null
  if (!firstItem) return 166
  const styles = window.getComputedStyle(carouselRef.value)
  const gapValue = parseFloat(styles.columnGap || styles.gap || '0')
  return firstItem.getBoundingClientRect().width + gapValue
}

const scrollPrev = () => {
  carouselRef.value?.scrollBy({ left: -getScrollStep(), behavior: 'smooth' })
}

const scrollNext = () => {
  carouselRef.value?.scrollBy({ left: getScrollStep(), behavior: 'smooth' })
}

const handleRemove = (draw: UpcomingDraw) => {
  const idx = upcomingDrawsStore.draws.findIndex((item) => item.id === draw.id)
  if (idx !== -1) {
    upcomingDrawsStore.draws.splice(idx, 1)
    if (upcomingDrawsStore.draws.length === 0) {
      void upcomingDrawsStore.fetchDraws(1)
    }
  }
  nextTick(() => {
    iframeCommunication.sendImmediateHeightUpdate()
  })
}

const handleViewAll = () => {
  iframeCommunication.sendMessage('widget-view-all', { view: 'all' })
}

const bootstrapWidgetContext = async () => {
  const account = route.query.account
  if (typeof account === 'string' && account.trim()) {
    authStore.account = account.trim()
    sessionStorage.setItem('auth_account', account.trim())
  }

  const currency = route.query.currency
  if (typeof currency === 'string' && currency.trim()) {
    sessionStorage.setItem('auth_currency', currency.split('?')[0])
  }

  try {
    await initializeTheme(true)
  } catch (themeError) {
    console.warn('Widget theme initialization failed:', themeError)
  }
}

let resizeObserver: ResizeObserver | null = null

watch(carouselRef, (track) => {
  resizeObserver?.disconnect()
  updateScrollState()
  if (track && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      updateScrollState()
      iframeCommunication.sendImmediateHeightUpdate()
    })
    resizeObserver.observe(track)
  }
}, { flush: 'post' })

const refetchDrawsAfterHandshake = () => {
  void upcomingDrawsStore.fetchDraws(1, undefined, true)
}

let stopParentReadyListener: (() => void) | null = null

onMounted(async () => {
  await bootstrapWidgetContext()

  authStore.refreshAuthFromExternalSources()
  stopParentReadyListener = iframeCommunication.on('parent-ready', refetchDrawsAfterHandshake)
  await upcomingDrawsStore.fetchDraws(1)

  await nextTick()

  iframeCommunication.startObserving('iframe-root')
  iframeCommunication.sendImmediateHeightUpdate()
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  stopParentReadyListener?.()
  stopParentReadyListener = null
})

watch(
  () => route.query.account,
  async (account) => {
    if (typeof account === 'string' && account.trim()) {
      authStore.account = account.trim()
      sessionStorage.setItem('auth_account', account.trim())
      try {
        await initializeTheme(true)
      } catch (themeError) {
        console.warn('Widget theme refresh failed:', themeError)
      }
    }
  }
)

watch(
  draws,
  () => {
    nextTick(() => {
      updateScrollState()
      iframeCommunication.sendImmediateHeightUpdate()
    })
  },
  { deep: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("h2", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, _toDisplayString(titleParts.value.lead), 1),
        _createElementVNode("span", _hoisted_5, _toDisplayString(titleParts.value.rest), 1)
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t('widget.nextDraws')), 1),
        (showAllLink.value)
          ? (_openBlock(), _createElementBlock("a", {
              key: 0,
              class: "widget-all-chip",
              href: "#",
              onClick: _withModifiers(handleViewAll, ["prevent"])
            }, _toDisplayString(_ctx.$t('widget.seeAll')), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_ctx.$t('common.loading')), 1))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(error.value), 1))
        : (draws.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_ctx.$t('widget.noUpcomingDraws')), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("div", {
                ref_key: "carouselRef",
                ref: carouselRef,
                class: "widget-track",
                onScrollPassive: updateScrollState
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(draws.value, (draw) => {
                  return (_openBlock(), _createBlock(WidgetLotteryCard, {
                    key: `${draw.game_slug}-${draw.id}`,
                    lottery: draw,
                    onRemove: handleRemove
                  }, null, 8, ["lottery"]))
                }), 128))
              ], 544),
              (canScrollPrev.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "widget-scroll-arrow widget-scroll-arrow--previous",
                    "aria-label": _ctx.$t('common.previous'),
                    onClick: scrollPrev
                  }, _cache[0] || (_cache[0] = [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("path", { d: "m15 6-6 6 6 6" })
                    ], -1)
                  ]), 8, _hoisted_12))
                : _createCommentVNode("", true),
              (canScrollNext.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "button",
                    class: "widget-scroll-arrow widget-scroll-arrow--next",
                    "aria-label": _ctx.$t('common.next'),
                    onClick: scrollNext
                  }, _cache[1] || (_cache[1] = [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("path", { d: "m9 6 6 6-6 6" })
                    ], -1)
                  ]), 8, _hoisted_13))
                : _createCommentVNode("", true)
            ]))
  ]))
}
}

})